import { useMemo, useState } from 'react';
import {
  Wand2, CalendarClock, PlusCircle, BadgeDollarSign, Globe, Tags,
  ArrowLeft, ArrowRight, Check, Download, Copy, Info, RotateCcw, ChevronDown, ChevronRight, HelpCircle, CheckCircle2,
} from 'lucide-react';
import {
  WIZARD_TASKS, getTask, initialAnswers, validateStepFields, fieldVisible, buildBatch,
} from '../../data/accWizard';

const ICONS = { CalendarClock, PlusCircle, BadgeDollarSign, Globe, Tags };

function Field({ field, value, error, onChange }) {
  const base = 'w-full rounded-lg border px-3.5 py-2.5 text-[15px] bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-400/40 ' +
    (error ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-300 focus:border-violet-400');
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-800 mb-1">{field.label}</label>
      {field.type === 'select' ? (
        <select className={base} value={value} onChange={(e) => onChange(e.target.value)}>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          className={base}
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          step={field.type === 'number' ? 'any' : undefined}
          placeholder={field.placeholder || ''}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {field.help && <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1"><HelpCircle className="w-3.5 h-3.5 flex-none mt-0.5 text-gray-400" />{field.help}</p>}
      {error && <p className="text-xs text-red-500 mt-1 font-medium">{error}</p>}
    </div>
  );
}

export default function AccWizard() {
  const [businessPartner, setBusinessPartner] = useState('Sunny Benefits');
  const [filename, setFilename] = useState('sunny_change');
  const [taskId, setTaskId] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [touched, setTouched] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showXml, setShowXml] = useState(false);

  const task = taskId ? getTask(taskId) : null;
  const isReview = task && step >= task.steps.length;
  const currentStep = task && !isReview ? task.steps[step] : null;
  const totalScreens = task ? task.steps.length + 1 : 1;

  const stepErrors = useMemo(
    () => (currentStep ? validateStepFields(currentStep, answers) : {}),
    [currentStep, answers],
  );
  const stepValid = Object.keys(stepErrors).length === 0;

  const fullXml = useMemo(
    () => (task ? buildBatch(businessPartner, [task.build(answers)]) : ''),
    [task, answers, businessPartner],
  );
  const safeName = (filename || 'acc_change').replace(/[^A-Za-z0-9_]/g, '_');

  function pickTask(id) {
    const t = getTask(id);
    setTaskId(id);
    setAnswers(initialAnswers(t));
    setStep(0);
    setTouched(false);
    setShowXml(false);
  }
  function setAnswer(id, val) { setAnswers((a) => ({ ...a, [id]: val })); }
  function next() {
    if (!stepValid) { setTouched(true); return; }
    setStep((s) => s + 1);
    setTouched(false);
  }
  function back() {
    if (step === 0) { setTaskId(null); return; }
    setStep((s) => s - 1);
    setTouched(false);
  }
  function restart() { setTaskId(null); setStep(0); setAnswers({}); setTouched(false); }
  function copyXml() { navigator.clipboard?.writeText(fullXml).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }
  function downloadXml() {
    const blob = new Blob([fullXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${safeName}.XML`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wand2 className="w-6 h-6 text-violet-500" />
          Configuration Wizard
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          Answer a few plain-English questions and we’ll create the FIS configuration file for you — no technical knowledge needed.
        </p>
      </div>

      {/* PICK A TASK */}
      {!task && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">What would you like to do?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {WIZARD_TASKS.map((t) => {
              const Icon = ICONS[t.icon] || Wand2;
              return (
                <button key={t.id} onClick={() => pickTask(t.id)}
                  className="text-left rounded-xl border border-gray-200 bg-white p-4 hover:border-violet-300 hover:shadow-sm transition">
                  <div className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center"><Icon className="w-5 h-5 text-violet-600" /></span>
                    <span className="font-semibold text-gray-900">{t.title}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">{t.tagline}</p>
                </button>
              );
            })}
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex gap-2">
            <Info className="w-4 h-4 flex-none mt-0.5" />
            <p>Pick a task above. We’ll walk you through it one question at a time, explain each step, and produce a ready-to-send file at the end. You can go back and change any answer.</p>
          </div>
        </div>
      )}

      {/* WIZARD FLOW */}
      {task && (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          {/* progress */}
          <div className="px-5 pt-4">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
              <span className="font-semibold text-gray-700">{task.title}</span>
              <span>{isReview ? 'Review' : `Question ${step + 1} of ${task.steps.length}`}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${((step + 1) / totalScreens) * 100}%` }} />
            </div>
          </div>

          {/* QUESTION */}
          {!isReview && (
            <div className="p-5 space-y-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{currentStep.question}</h3>
                {currentStep.help && (
                  <div className="mt-2 rounded-lg bg-violet-50 border border-violet-100 px-3.5 py-2.5 text-sm text-violet-900 flex gap-2">
                    <Info className="w-4 h-4 flex-none mt-0.5 text-violet-500" />
                    <span>{currentStep.help}</span>
                  </div>
                )}
                {step === 0 && task.intro && <p className="text-sm text-gray-500 mt-2">{task.intro}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {currentStep.fields.filter((f) => fieldVisible(f, answers)).map((f) => (
                  <Field key={f.id} field={f} value={answers[f.id] ?? ''} error={touched ? stepErrors[f.id] : undefined} onChange={(v) => setAnswer(f.id, v)} />
                ))}
              </div>
            </div>
          )}

          {/* REVIEW */}
          {isReview && (
            <div className="p-5 space-y-5">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" /> Please review your changes
                </h3>
                <p className="text-sm text-gray-500 mt-1">Here’s a plain summary of what this file will do. Go back if anything looks off.</p>
              </div>

              <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                {task.review(answers).filter((r) => r.value !== '' && r.value != null).map((r) => (
                  <div key={r.label} className="flex items-start gap-4 px-4 py-2.5">
                    <span className="text-sm text-gray-500 w-40 flex-none">{r.label}</span>
                    <span className="text-sm font-medium text-gray-900">{r.value}</span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Company / client</label>
                  <input className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-violet-400/40" value={businessPartner} onChange={(e) => setBusinessPartner(e.target.value)} />
                  <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1"><HelpCircle className="w-3.5 h-3.5 flex-none mt-0.5 text-gray-400" />Your organization’s top-level name in FIS. Usually pre-filled correctly.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">File name</label>
                  <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden">
                    <input className="px-3.5 py-2.5 text-[15px] focus:outline-none flex-1" value={filename} onChange={(e) => setFilename(e.target.value)} />
                    <span className="px-2 text-xs text-gray-400 bg-gray-50 border-l border-gray-200 py-2.5">.XML</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1"><HelpCircle className="w-3.5 h-3.5 flex-none mt-0.5 text-gray-400" />Letters, numbers and underscores only — no spaces.</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button onClick={downloadXml} className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold rounded-lg px-4 py-2.5 hover:bg-violet-700">
                  <Download className="w-4 h-4" /> Download the file
                </button>
                <button onClick={copyXml} className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 rounded-lg px-4 py-2.5 hover:bg-gray-50">
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy text'}
                </button>
              </div>

              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex gap-2">
                <Info className="w-4 h-4 flex-none mt-0.5" />
                <p><strong>What next?</strong> Download the file and send it to FIS the usual way (drop it on the MoveIT folder). FIS processes it and returns a log so you can confirm it worked.</p>
              </div>

              {/* advanced peek */}
              <div className="rounded-xl border border-gray-200">
                <button onClick={() => setShowXml((s) => !s)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-500 hover:text-gray-700">
                  {showXml ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Show the technical file we created (optional)
                </button>
                {showXml && (
                  <pre className="text-[12px] leading-relaxed font-mono whitespace-pre overflow-auto p-4 bg-slate-900 text-slate-300 rounded-b-xl">{fullXml}</pre>
                )}
              </div>
            </div>
          )}

          {/* footer nav */}
          <div className="px-5 py-3.5 border-t border-gray-100 bg-gray-50/60 flex items-center justify-between">
            <button onClick={back} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-4 h-4" /> {step === 0 ? 'Choose another task' : 'Back'}
            </button>
            <div className="flex items-center gap-3">
              <button onClick={restart} className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600">
                <RotateCcw className="w-3.5 h-3.5" /> Start over
              </button>
              {!isReview && (
                <button onClick={next} disabled={touched && !stepValid}
                  className="inline-flex items-center gap-1.5 bg-violet-600 text-white font-semibold rounded-lg px-4 py-2 hover:bg-violet-700 disabled:opacity-50">
                  {step === task.steps.length - 1 ? 'Review' : 'Next'} <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
